import React, { useContext, useState } from "react";
// import { faArrowLeft, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import { NavigationContext } from "navigation-react";
import LoaderAnimation from "./LoaderAnimationLarge";
import SecureLS from "secure-ls";
import SearchFilter from "./SearchFilter";
import TableTotalNCost from "./TableTotalNCost";
import EditPatientRegister from "./EditPatientRegister";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClose } from "@fortawesome/free-solid-svg-icons";
import MessageModal from "./MessageModal";

const ls = new SecureLS({ encodingType: "aes" });

function parseDate(dateStr) {
  if (!dateStr) return null;

  // First try ISO format (from API)
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) return isoDate;

  // Try DD/MM/YYYY format (from input)
  const parts = dateStr.split(/[/-]/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    // Handle two-digit years
    const fullYear = year < 100 ? 2000 + year : year;
    const parsedDate = new Date(fullYear, month, day);
    if (!isNaN(parsedDate.getTime())) return parsedDate;
  }

  return null;
}

const PatientRegister = () => {
  // eslint-disable-next-line no-unused-vars
  const { stateNavigator } = useContext(NavigationContext);
  const userRoles = ls.get("currentuserrole");
  // eslint-disable-next-line no-unused-vars
  const [userRole, setUserRole] = useState(userRoles);
  const [searchTerm, setSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [patientStartDate, setPatientStartDate] = useState("");
  const [patientEndDate, setPatientEndDate] = useState("");
  const toggleModalEditPatient = () => setModalEditPatient(!modalEditPatient);
  const [modalEditPatient, setModalEditPatient] = useState(false);
  const [selectedPatientID, setSelectedPatientID] = useState(null);

  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const toggleModalSuccessMsg = () => {
    setModalSuccessMsg(!modalSuccessMsg);
  };

  // Determine the correct API URL based on the role
  let patientApiUrl = "";

  if (userRole.includes("content_editor")) {
    patientApiUrl = `${process.env.REACT_APP_PATIENT_REGISTER_MANAGER_API}?_format=json`;
  } else {
    patientApiUrl = `${process.env.REACT_APP_PATIENT_REGISTER_REQUESTER_API}?_format=json`;
  }

  const username1 = ls.get("username");
  const userpw = ls.get("userpw");

  const fetcher = (url) =>
    fetch(url, {
      credentials: "same-origin",
      mode: "same-origin",
      headers: {
        Authorization: `Basic ${btoa(`${username1}:${userpw}`)}`,
        "Content-Type": "application/json",
      },
    }).then((res) => res.json());

  const {
    data: patients,
    error,
    isLoading,
  } = useSWR(`${patientApiUrl}`, fetcher);

  const filteredData = Array.isArray(patients)
    ? patients.filter((patient) => {
        // Search term matching
        const matchesSearchTerm =
          patient.field_patient_id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          patient.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          patient.title_1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          patient.field_investigation_price
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          patient.field_payment_amount
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          patient.field_payment_amount_1
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          patient.field_assigned_radiologist
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          patient.field_patient_email_address
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          patient.field_patient_phone_number
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

        // Gender filter
        const matchesGender =
          !genderFilter ||
          patient.field_gender?.toLowerCase() === genderFilter.toLowerCase();

        // Date filtering
        const registeredDate = parseDate(patient.created);
        const startDate = parseDate(patientStartDate);
        const endDate = parseDate(patientEndDate);

        // If no date filters are applied, skip date comparison
        if (!startDate && !endDate) {
          return matchesSearchTerm && matchesGender;
        }

        // If we can't parse the registered date but have date filters, exclude
        if (!registeredDate) return false;

        // Normalize dates for comparison
        const regDate = new Date(registeredDate);
        regDate.setHours(0, 0, 0, 0);

        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (regDate < start) return false;
        }

        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (regDate > end) return false;
        }

        return matchesSearchTerm && matchesGender;
      })
    : [];

  const LoadingError = () => (
    <div className="loadingindicator">
      <p>Failed to load patient register. Please try again.</p>
    </div>
  );

  if (isLoading) return <LoaderAnimation />;
  if (error) return <LoadingError />;

  return (
    <>
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div className="patient-register">
            <TopActionMenus AddTitle="Patient register" />
            <SearchFilter
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              genderFilter={genderFilter}
              setGenderFilter={setGenderFilter}
              patientStartDate={patientStartDate}
              setPatientStartDate={setPatientStartDate}
              patientEndDate={patientEndDate}
              setPatientEndDate={setPatientEndDate}
            />
            {filteredData.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <td>Patient ID</td>
                    <td>Patient Full Name</td>
                    <td>Gender</td>
                    <td>Date of Birth</td>
                    <td>Contact Number</td>
                    <td>Email Address</td>
                    {userRole[1].includes("content_editor") ? (
                      <td>Registered by</td>
                    ) : null}
                    <td>Registered Date</td>
                    <td>Action</td>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((patient) => (
                    <tr key={patient.field_patient_id}>
                      <td>{patient.field_patient_id || "n/a"}</td>
                      <td>{patient.title}</td>
                      <td>{patient.field_gender}</td>
                      <td>{patient.field_date_of_birth}</td>
                      <td>{patient.field_patient_phone_number}</td>
                      <td>{patient.field_patient_email_address}</td>
                      {userRole[1].includes("content_editor") ? (
                        <td>{patient.uid}</td>
                      ) : null}
                      <td>{patient.created}</td>
                      <td className="formActionBtn">
                        <button
                          onClick={() => {
                            setSelectedPatientID(patient);
                            toggleModalEditPatient();
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <LoaderAnimation loadingTxt="No patients found matching your criteria" />
            )}
            {filteredData?.length > 0 && (
              <TableTotalNCost
                noOfReportLable="Number of patient :"
                noOfReportLength={filteredData?.length}
              />
            )}
          </div>
        </div>
        <DashboardContentFooter />
      </div>

      {modalEditPatient && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content">
            <div className="modal-body">
              <div className="modal-header">
                {/* <p>
                      {`Edit CT - Scan report for ${
                        patientData?.title?.[0]?.value || "N/A"
                      }`}
                    </p> */}
                <p>Edit Patient</p>
                <button
                  className="close-modal"
                  onClick={() => {
                    toggleModalEditPatient();
                  }}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
              <EditPatientRegister
                editPatientDataID={selectedPatientID}
                toggleModalEditPatient={toggleModalEditPatient}
                setModalSuccessMsg={setModalSuccessMsg}
                refreshPatientAfterEdit={() => mutate(`${patientApiUrl}`)}
              />
            </div>
          </div>
        </div>
      )}
      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          fontSize={20}
          message="Patient updated successfully!"
          onClose={() => setModalSuccessMsg(false, toggleModalSuccessMsg())}
          rightIcon={faClose}
        />
      )}
    </>
  );
};

export default PatientRegister;
